import { useState, useEffect, useRef, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { useDebounce } from "@/hooks/useDebounce";
import { routes } from "@/app/navigation/routes";
import { propertySupportService } from "@/features/properties/model/propertyServices";
import type { PropertyRealStateDetailInfo } from "@/features/properties/model/propertiesTypes";
import {
  addGuestProperty,
  isValidUUID,
  type GuestPropertyItem,
} from "@/features/properties/model/guestSession";
import styles from "./AddressSearch.module.css";

export interface AddressSuggestion {
  id: string;
  address: string;
  city?: string;
  state?: string;
  zipCode?: string;
  apn?: string;
  propertyDetails?: PropertyRealStateDetailInfo;
}

export interface AddressSearchProps {
  placeholder?: string;
  buttonText?: string;
  onAddressSelect?: (address: AddressSuggestion) => void;
  onButtonClick?: (address: string, selectedSuggestion?: AddressSuggestion | null) => void;
  className?: string;
  searchAddresses?: (query: string) => Promise<AddressSuggestion[]>;
  preventInternalSubmit?: boolean;
  isSubmitting?: boolean;
  redirectTo?: string;
}

/**
 * AddressSearch Component
 * Reusable address search component with autocomplete functionality
 * Triggers search after 3+ characters are entered
 */
export const AddressSearch = ({
  placeholder = "Enter address",
  buttonText = "Get Estimate Savings",
  onAddressSelect,
  onButtonClick,
  className = "",
  searchAddresses,
  preventInternalSubmit = false,
  isSubmitting = false,
  redirectTo = routes.homepage,
}: AddressSearchProps) => {
  const [inputValue, setInputValue] = useState("");
  const [suggestions, setSuggestions] = useState<AddressSuggestion[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [isFetchingDetails, setIsFetchingDetails] = useState(false);
  const [showSuggestions, setShowSuggestions] = useState(false);
  const [selectedIndex, setSelectedIndex] = useState(-1);
  const [selectedAddress, setSelectedAddress] = useState<AddressSuggestion | null>(null);
  const inputRef = useRef<HTMLInputElement>(null);
  const suggestionsRef = useRef<HTMLDivElement>(null);
  const suppressNextFetchRef = useRef(false);
  const navigate = useNavigate();

  const debouncedQuery = useDebounce(inputValue, 300);

  // Check if query has at least 3 characters (including digits, letters, etc.)
  const isValidQuery = debouncedQuery.trim().length >= 3;

  // Default search function (mock - can be replaced with real API)
  const defaultSearchAddresses = useCallback(
    async (query: string): Promise<AddressSuggestion[]> => {
      // Mock delay to simulate API call
      await new Promise((resolve) => setTimeout(resolve, 300));

      // Mock suggestions based on query
      const mockSuggestions: AddressSuggestion[] = [
        {
          id: "1",
          address: `${query} 1/2 Chestnut St`,
          city: "Kearny",
          state: "NJ",
          zipCode: "07032",
        },
        {
          id: "2",
          address: `${query} 1/2 Chestnut St`,
          city: "Kearny",
          state: "NJ",
          zipCode: "07033",
        },
        {
          id: "3",
          address: `${query} 1/2 Chestnut St`,
          city: "Kearny",
          state: "NJ",
          zipCode: "07034",
        },
        {
          id: "4",
          address: `${query} 1/2 Chestnut St`,
          city: "Kearny",
          state: "NJ",
          zipCode: "07035",
        },
      ];

      return mockSuggestions;
    },
    []
  );

  const searchFunction = searchAddresses || defaultSearchAddresses;

  // Fetch suggestions when debounced query changes
  useEffect(() => {
    const fetchSuggestions = async () => {
      // When a user selects a suggestion, we update the input value which would normally
      // trigger another autocomplete request. Suppress that one follow-up fetch.
      if (suppressNextFetchRef.current) {
        suppressNextFetchRef.current = false;
        return;
      }
      if (!isValidQuery) {
        setSuggestions([]);
        setShowSuggestions(false);
        return;
      }

      setIsLoading(true);
      try {
        const results = await searchFunction(debouncedQuery.trim());
        console.log("AddressSearch: Received results:", results);
        console.log("AddressSearch: Results length:", results?.length);
        setSuggestions(results || []);
        // Keep dropdown visible for valid queries even when empty, so we can show "No address found".
        setShowSuggestions(true);
        setSelectedIndex(-1);
      } catch (error) {
        console.error("Error fetching address suggestions:", error);
        setSuggestions([]);
        setShowSuggestions(true);
      } finally {
        setIsLoading(false);
      }
    };

    fetchSuggestions();
  }, [debouncedQuery, isValidQuery, searchFunction]);

  // Handle input change
  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value;
    setInputValue(value);
    // Only hide suggestions if input becomes too short, otherwise let useEffect handle showing them
    if (value.trim().length < 3) {
      setShowSuggestions(false);
    }
    // Clear selected address if user types something new
    if (selectedAddress) {
      setSelectedAddress(null);
    }
  };

  // Handle suggestion selection
  const handleSelectSuggestion = (suggestion: AddressSuggestion) => {
    const fullAddress = formatAddress(suggestion);
    suppressNextFetchRef.current = true;
    setInputValue(fullAddress);
    setShowSuggestions(false);
    setSelectedIndex(-1);
    setSelectedAddress(suggestion);
    onAddressSelect?.(suggestion);
  };

  // Format address for display
  const formatAddress = (suggestion: AddressSuggestion): string => {
    const parts = [suggestion.address];
    if (suggestion.city && !suggestion.address.includes(suggestion.city))
      parts.push(suggestion.city);
    if (suggestion.state && !suggestion.address.includes(suggestion.state))
      parts.push(suggestion.state);
    if (suggestion.zipCode && !suggestion.address.includes(suggestion.zipCode))
      parts.push(suggestion.zipCode);
    return parts.join(", ");
  };

  // Handle button click (Explicit "Get Estimate" trigger)
  const handleButtonClick = async () => {
    if (isFetchingDetails || !inputValue.trim()) {
      return;
    }

    onButtonClick?.(inputValue, selectedAddress);

    if (preventInternalSubmit) {
      return;
    }

    setIsFetchingDetails(true);

    try {
      const fullAddress = selectedAddress ? formatAddress(selectedAddress) : inputValue.trim();
      const zipMatch = fullAddress.match(/\b\d{5}\b/);
      const zip = selectedAddress?.zipCode || (zipMatch ? zipMatch[0] : undefined);
      const apn = selectedAddress?.apn || undefined;

      let propertyDetails: PropertyRealStateDetailInfo | null = null;
      try {
        propertyDetails = await propertySupportService.getRealStatePropertyDetails({
          apn,
          zip,
          address: fullAddress,
          comps: true,
        });
      } catch (err) {
        console.warn("Failed to fetch real estate property details:", err);
      }

      // Explicit "Get Estimate" button click trigger for POST /api/property-service/properties
      const addr = selectedAddress;
      const streetAddress = addr?.address || fullAddress.split(",")[0]?.trim() || fullAddress;
      const cityStateZip =
        [addr?.city, addr?.state, addr?.zipCode].filter(Boolean).join(", ") ||
        fullAddress.split(",").slice(1).join(",").trim();

      const parcelId = addr?.apn || apn || undefined;
      const initialPropId = addr?.id && isValidUUID(addr.id) ? addr.id : crypto.randomUUID();

      const assessedVal =
        propertyDetails?.taxInfo?.assessedValue ||
        propertyDetails?.taxInfo?.assessedImprovementValue ||
        76300;
      const marketVal =
        propertyDetails?.taxInfo?.marketValue ||
        propertyDetails?.taxInfo?.marketImprovementValue ||
        21800;
      const potentialSav = Math.round(assessedVal * 0.0042) || 323;

      const newItem: Omit<GuestPropertyItem, "selected"> = {
        property_id: initialPropId,
        address: fullAddress,
        streetAddress,
        cityStateZip,
        city: addr?.city || "Marietta",
        state: addr?.state || "OH",
        zip: addr?.zipCode || "45750",
        assessedValue: Number(assessedVal),
        marketValue: Number(marketVal),
        potentialSavings: Number(potentialSav),
        potentialSavingsPercent: 4.23,
        propertyType: "Residential",
        yearBuilt: 1998,
        parcelId: parcelId || initialPropId,
        lotSize: "0.45 Acres",
        taxYear: 2024,
        squareFeet: "2,150 sqft",
        schoolDistrict: "Marietta City Schools",
        ownerDetails: {
          ownerName: "Property Owner",
          email: "owner@example.com",
          mailingAddress: fullAddress,
          phone: "(555) 123-4567",
        },
        rawDetails: propertyDetails || undefined,
      };

      const savedProperty = await addGuestProperty(newItem);
      const effectivePropertyId = savedProperty?.property_id || newItem.property_id;

      if (effectivePropertyId) {
        window.localStorage.setItem("selectedAddress", effectivePropertyId);
      }

      navigate(redirectTo, {
        state: {
          addressData: {
            address: selectedAddress || {
              id: effectivePropertyId,
              address: fullAddress.split(",")[0]?.trim() || fullAddress,
              zipCode: zip,
            },
            addressString: inputValue,
          },
          propertyDetails,
          propertyId: effectivePropertyId,
        },
      });
    } catch (error) {
      console.error("Error in property search button click:", error);
    } finally {
      setIsFetchingDetails(false);
    }
  };

  // Handle keyboard navigation
  const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (!showSuggestions || suggestions.length === 0) {
      if (e.key === "Enter") {
        handleButtonClick();
      }
      return;
    }

    switch (e.key) {
      case "ArrowDown":
        e.preventDefault();
        setSelectedIndex((prev) => (prev < suggestions.length - 1 ? prev + 1 : prev));
        break;
      case "ArrowUp":
        e.preventDefault();
        setSelectedIndex((prev) => (prev > 0 ? prev - 1 : -1));
        break;
      case "Enter":
        e.preventDefault();
        if (selectedIndex >= 0 && selectedIndex < suggestions.length) {
          const selectedSuggestion = suggestions[selectedIndex];
          if (selectedSuggestion) {
            handleSelectSuggestion(selectedSuggestion);
          }
        } else {
          handleButtonClick();
        }
        break;
      case "Escape":
        setShowSuggestions(false);
        setSelectedIndex(-1);
        inputRef.current?.blur();
        break;
    }
  };

  // Close suggestions when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (
        suggestionsRef.current &&
        !suggestionsRef.current.contains(event.target as Node) &&
        inputRef.current &&
        !inputRef.current.contains(event.target as Node)
      ) {
        setShowSuggestions(false);
        setSelectedIndex(-1);
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, []);

  return (
    <div className={`${styles.searchContainer} ${className}`}>
      <div className={styles.inputWrapper}>
        <div className={styles.iconCircle}>
          <svg
            className={styles.locationIcon}
            width="18"
            height="18"
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            strokeWidth="2.2"
            strokeLinecap="round"
            strokeLinejoin="round"
          >
            <path d="M21 10c0 7-9 13-9 13s-9-6-9-13a9 9 0 0 1 18 0z" />
            <circle cx="12" cy="10" r="3" />
          </svg>
        </div>
        <input
          ref={inputRef}
          type="text"
          className={styles.addressInput}
          placeholder={placeholder}
          value={inputValue}
          onChange={handleInputChange}
          onKeyDown={handleKeyDown}
          onFocus={() => {
            if (isValidQuery && suggestions.length > 0) {
              setShowSuggestions(true);
            }
          }}
        />
        {inputValue && (
          <button
            type="button"
            className={styles.clearBtn}
            onClick={() => {
              setInputValue("");
              setSelectedAddress(null);
              setShowSuggestions(false);
              inputRef.current?.focus();
            }}
            aria-label="Clear search"
          >
            <svg
              width="14"
              height="14"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth="2.5"
              strokeLinecap="round"
              strokeLinejoin="round"
            >
              <line x1="18" y1="6" x2="6" y2="18" />
              <line x1="6" y1="6" x2="18" y2="18" />
            </svg>
          </button>
        )}
        <button
          className={styles.estimateButton}
          onClick={handleButtonClick}
          disabled={(!selectedAddress && !inputValue.trim()) || isFetchingDetails || isSubmitting}
        >
          {isFetchingDetails || isSubmitting ? (
            "Loading..."
          ) : (
            <>
              <span>{buttonText}</span>
              <svg
                width="15"
                height="15"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth="2.5"
                strokeLinecap="round"
                strokeLinejoin="round"
              >
                <line x1="5" y1="12" x2="19" y2="12" />
                <polyline points="12 5 19 12 12 19" />
              </svg>
            </>
          )}
        </button>
      </div>
      {showSuggestions && (
        <div ref={suggestionsRef} className={styles.suggestionsContainer}>
          {isLoading && (
            <div className={styles.loadingMessage}>
              <svg
                className={styles.spinIcon}
                width="16"
                height="16"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth="2.5"
              >
                <path d="M21 12a9 9 0 1 1-6.219-8.56" />
              </svg>
              <span>Searching addresses...</span>
            </div>
          )}
          {!isLoading && suggestions.length === 0 && isValidQuery && (
            <div className={styles.emptyMessage}>
              <svg
                width="18"
                height="18"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth="2"
                strokeLinecap="round"
                strokeLinejoin="round"
              >
                <circle cx="11" cy="11" r="8" />
                <line x1="21" y1="21" x2="16.65" y2="16.65" />
              </svg>
              <span>No address found. Try street name or ZIP code.</span>
            </div>
          )}
          {!isLoading &&
            suggestions.length > 0 &&
            suggestions.map((suggestion, index) => {
              const formatted = formatAddress(suggestion);
              const [line1, ...rest] = formatted.split(",");
              const line2 = rest.join(",").trim();

              return (
                <div
                  key={suggestion.id}
                  className={`${styles.suggestionItem} ${
                    index === selectedIndex ? styles.suggestionItemActive : ""
                  }`}
                  onClick={() => handleSelectSuggestion(suggestion)}
                  onMouseEnter={() => setSelectedIndex(index)}
                >
                  <div className={styles.suggestionIconWrap}>
                    <svg
                      width="16"
                      height="16"
                      viewBox="0 0 24 24"
                      fill="none"
                      stroke="currentColor"
                      strokeWidth="2"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                    >
                      <path d="M21 10c0 7-9 13-9 13s-9-6-9-13a9 9 0 0 1 18 0z" />
                      <circle cx="12" cy="10" r="3" />
                    </svg>
                  </div>
                  <div className={styles.suggestionText}>
                    <span className={styles.suggestionLine1}>{line1}</span>
                    {line2 && <span className={styles.suggestionLine2}>{line2}</span>}
                  </div>
                </div>
              );
            })}
        </div>
      )}
    </div>
  );
};
